import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useCallback, useState } from "react";
import CustomSelect, { Value } from "../../../../../components/CustomSelect";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import { MaskMultiSelectScroll } from "../../../../../components/MaskMultiSelectScroll";
import NumberInput from "../../../../../components/NumberInput";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import { ScrollArea } from "../../../../../components/ScrollBar";
import Text from "../../../../../components/Text";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useFetchClaimsByUuids } from "../../../../../queries/billings/claims/use-get-claims-by-uuids";
import { useGetClaimsV2 } from "../../../../../queries/billings/claims/use-get-claims-v2";
import { CLAIM_TYPE } from "../claims/claims-constants";
import { PRIMITIVE } from "../../../../../tokens/design-tokens";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DEFAULT_FONTSIZE,
  DEFAULT_TEXT_FONT_WEIGHT,
  DGT_Pagination,
} from "../../../../../utils/utils";
import {
  ADJUSTMENT_TYPE_OPTIONS,
  CLAIM_STATUS_OPTIONS,
  ERA_POSTING_STATUS_BADGE,
  EraPostingDisplayStatus,
  PAID_AS_OPTIONS,
  normalizeEraPostingStatus,
} from "./constants";

// CPT-level state comes from the single `postingStatus` key the backend sends
// (UNPAID / PARTIALLY_PAID / PAID / DENIED). A line counts as posted once it has
// been adjudicated either way: PAID, or DENIED — a denial is a posted outcome,
// not an unposted one.
export const isCptPosted = (pc: ProcedureCode) => {
  const status = normalizeEraPostingStatus(pc.postingStatus);
  return status === "PAID" || status === "DENIED";
};

// ECRH-4700 — derive claim posting status from how many CPT lines are posted.
const derivePostingFromPcs = (
  pcs: ProcedureCode[]
): EraPostingDisplayStatus => {
  const postedCount = pcs.filter(isCptPosted).length;
  if (pcs.length > 0 && postedCount === pcs.length) return "PAID";
  if (postedCount > 0) return "PARTIALLY_PAID";
  return "NOT_PAID";
};

// How far along each bucket is, so the two sources below can be compared.
const POSTING_RANK: Record<EraPostingDisplayStatus, number> = {
  NOT_PAID: 0,
  DENIED: 1,
  PARTIALLY_PAID: 2,
  PAID: 3,
};

// The claim-level status the backend sends on `postingStatus` is authoritative
// on load, but it goes stale the moment a CPT line is posted on this screen —
// the claim is not refetched, so it would keep reading "Not Posted" while lines
// are being posted. Taking whichever source is further along keeps the badge
// live without ignoring the backend value.
const getClaimPostingDisplay = (
  pcs: ProcedureCode[],
  backendStatus?: string
): EraPostingDisplayStatus => {
  const derived = derivePostingFromPcs(pcs);
  const fromBackend = normalizeEraPostingStatus(backendStatus);
  if (!fromBackend) return derived;
  return POSTING_RANK[derived] > POSTING_RANK[fromBackend]
    ? derived
    : fromBackend;
};

const ClaimPostingBadge = ({
  pcs,
  postingStatus,
}: {
  pcs: ProcedureCode[];
  postingStatus?: string;
}) => {
  const cfg =
    ERA_POSTING_STATUS_BADGE[getClaimPostingDisplay(pcs, postingStatus)];
  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        border: "none",
        fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
        height: 22,
        fontSize: "11px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const toNum = (v: unknown) => Number(v) || 0;

// ECRH-5391 — Balance = Charge − (Payment + Adjustment + Patient Responsibility + Write-off),
// where Patient Responsibility is Copay + Co-Insurance + Deductible.
export const getPatientResponsibility = (pc: ProcedureCode) =>
  (pc.copay || 0) + (pc.coInsurance || 0) + (pc.deductible || 0);

export const getBalance = (pc: ProcedureCode) =>
  (pc.charge || 0) -
  ((pc.insurancePaid || 0) +
    (pc.adjustment || 0) +
    getPatientResponsibility(pc) +
    (pc.writeOff || 0));

// ECRH-5571 — a non-denied CPT line may be posted only once its Charge is
// settled (Scenario 3), and settled simply means the Balance on the row reads
// zero: every column that takes money off the Charge counts — Payment,
// Adjustment, Patient Responsibility and Write-off.
//
// The original ECRH-5474 rule counted only Payment + Adjustment, so a charge
// settled through Deductible, Copay or a Write-off was blocked while the row
// displayed Balance 0. Deriving the check from `getBalance` keeps the rule and
// the number on screen from ever disagreeing.
const isChargeSettled = (pc: ProcedureCode) => Math.abs(getBalance(pc)) < 0.001;

// Allowed = Charge − (Adjustment + Write-off). No column of its own on this
// form; the value is what the claim's procedure table shows as "Allowed ($)".
export const getAllowedAmount = (pc: ProcedureCode) =>
  (pc.charge || 0) - ((pc.adjustment || 0) + (pc.writeOff || 0));

// The Adjustment amount is split by the row's own "Adjustment type" dropdown:
// Contract -> Contract Adj, everything else (Other / Write-Off) -> Other Adj.
export const getContractAdjustment = (pc: ProcedureCode) =>
  pc.adjustmentType === "Contract" ? pc.adjustment || 0 : 0;

export const getOtherAdjustment = (pc: ProcedureCode) =>
  pc.adjustmentType === "Contract" ? 0 : pc.adjustment || 0;

// Denial codes are free text: alphanumeric, hyphen allowed. (ECRH-5391)
// const DENIAL_CODE_PATTERN = /^[A-Za-z0-9-]+$/;

// Red asterisk marking a required field.
// const RequiredMark = () => (
//   <Box component="span" sx={{ color: "error.main" }}>
//     *
//   </Box>
// );

export interface SelectedClaim {
  claimUuid: string;
  claimId: string;
  patientName: string;
  dateOfService: string;
  provider: string;
  status: string;
  paidAs: string;
  expanded: boolean;
  posted: boolean;
  // Claim-level posting status from the backend (UNPAID / PARTIALLY_PAID /
  // PAID). Drives the "Posting" column badge.
  postingStatus?: string;
  totalCharge?: number;
  patientProcedureCodes: ProcedureCode[];
}

export interface ProcedureCode {
  uuid: string;
  cptCode: string;
  description: string;
  quantity: number;
  unitCharge: number;
  charge: number;
  insurancePaid: number;
  adjustment: number;
  adjustmentType: string;
  writeOff: number;
  writeOffType: string;
  copay: number;
  coInsurance: number;
  deductible: number;
  comments: string;
  denied: boolean;
  // ECRH-5391 — free-text alphanumeric denial code, captured only for denied CPTs.
  denialCode: string;
  remainingBalance?: number;
  // ECRH-4700 — per-CPT posting state. Single source of truth, same enum the
  // Claims list shows in its Posting Status column (UNPAID / PARTIALLY_PAID /
  // PAID / DENIED). Read it through `isCptPosted`, never directly — it replaces
  // the old `posted` boolean.
  postingStatus?: string;
}

interface SelectedClaimsSectionProps {
  selectedClaims: SelectedClaim[];
  setSelectedClaims: React.Dispatch<React.SetStateAction<SelectedClaim[]>>;
  // ECRH-4700 — posting is per CPT line (claim-level Post removed).
  onPostCpt: (claimUuid: string, pcUuid: string) => void;
  enabled: boolean;
  // Payer the EOB was created against. The Select Claim dropdown may only offer
  // claims billed to this payer, so the list stays closed until it is known.
  payerId?: string;
  // EOB amount, sent to the claim list as the `remainingBalance` filter so the
  // dropdown only offers claims that still have money owing against them.
  remainingBalance?: number;
}

interface ClaimOption {
  [key: string]: string;
  value: string;
  label: string;
  patientName: string;
  dateOfService: string;
}

/**
 * A claim as the backend returns it, shaped into the row the EOB screen edits.
 * Shared by the Add Claims flow and the prefill that restores an EOB's already
 * associated claims when it is reopened.
 */
export const mapClaimToSelectedClaim = (claim: any): SelectedClaim => ({
  claimUuid: claim.uuid ?? "",
  claimId: claim.claimId ?? claim.uuid?.split("-")?.[0]?.toUpperCase() ?? "",
  patientName: claim.patientName ?? "-",
  dateOfService: claim.dateOfService ?? "",
  provider: claim.providerName ?? "-",
  status: claim.claimStatus ?? "",
  paidAs: "",
  expanded: false,
  posted: false,
  postingStatus: claim.postingStatus ?? undefined,
  totalCharge: claim.totalCharge ?? claim.total ?? undefined,
  patientProcedureCodes: (claim.patientProcedureCodes ?? []).map((pc: any) => {
    const procedureCodeObj = pc.procedureCode ?? {};
    const procedureCodeEntry =
      (Object.values(procedureCodeObj)?.[0] as string) ?? "";
    const [cptCode = "", ...descParts] = procedureCodeEntry.split(" - ");
    const description = descParts.join(" - ");

    const quantity = toNum(pc.quantity) || 1;
    const unitCharge = toNum(pc.charges ?? pc.total);
    const copayBE = toNum(pc.copay);
    const coInsBE = toNum(pc.coInsurance);
    const deductBE = toNum(pc.deductible);
    const hasSplitPR = copayBE > 0 || coInsBE > 0 || deductBE > 0;
    const combinedPR = toNum(pc.patientResponsibility);

    return {
      uuid: pc.uuid ?? "",
      cptCode: cptCode.trim(),
      description: description.trim(),
      quantity,
      unitCharge,
      charge: unitCharge * quantity,
      insurancePaid: toNum(pc.insurancePaid),
      adjustment: toNum(pc.adjustment),
      adjustmentType: "Contract",
      writeOff: toNum(pc.writeOff),
      writeOffType: "Contract",
      copay: hasSplitPR ? copayBE : combinedPR,
      coInsurance: hasSplitPR ? coInsBE : 0,
      deductible: hasSplitPR ? deductBE : 0,
      comments: pc.comments ?? "",
      // The Denied checkbox is the user's input on an unposted line; on
      // a line the backend already posted it just mirrors DENIED.
      denied: normalizeEraPostingStatus(pc.postingStatus) === "DENIED",
      denialCode: pc.denialCode ?? "",
      remainingBalance: pc.remainingBalance ?? undefined,
      postingStatus: pc.postingStatus ?? undefined,
    };
  }),
});

const SelectedClaimsSection = ({
  selectedClaims,
  setSelectedClaims,
  onPostCpt,
  enabled,
  payerId,
  remainingBalance,
}: SelectedClaimsSectionProps) => {
  const [searchClaimKey, setSearchClaimKey] = useDebounce<string>("");
  const [claimPagination, setClaimPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [selectedClaimOptions, setSelectedClaimOptions] = useState<
    ClaimOption[]
  >([]);
  const [isAddingClaims, setIsAddingClaims] = useState(false);
  const fetchClaimsByUuids = useFetchClaimsByUuids();

  const { data: claimListData, isFetching: isFetchingClaims } = useGetClaimsV2({
    pagination: claimPagination,
    gridSorting: [],
    searchKey: searchClaimKey || undefined,
    // EOB posting requires the claim to have actually gone out to the payer and
    // to have nothing posted against it yet, so the dropdown lists Submitted
    // paper claims with an Unpaid posting status only.
    // claimStatuses: "SUBMITTED,ACCEPTED,DENIED,REJECTED",
    claimStatuses: "SUBMITTED",
    postingStatuses: "UNPAID",
    claimType: [CLAIM_TYPE.PaperClaim],
    paymentPosted: false,
    payerId,
    remainingBalance,
    enabled: enabled && Boolean(payerId),
  });

  const claimOptions: ClaimOption[] =
    (claimListData as any)?.data?.content
      ?.map((claim: any) => ({
        value: claim.uuid,
        label:
          claim.claimId ?? claim.uuid?.split("-")?.[0]?.toUpperCase() ?? "",
        patientName: claim.patientName ?? "",
        dateOfService: claim.dateOfService ?? "",
      }))
      ?.filter(
        (opt: ClaimOption) =>
          !selectedClaims.some((sc) => sc.claimUuid === opt.value)
      ) ?? [];

  const handleAddClaims = async () => {
    if (selectedClaimOptions.length === 0) return;

    const uuids = selectedClaimOptions.map((opt) => opt.value);
    setIsAddingClaims(true);

    try {
      const response = await fetchClaimsByUuids(uuids);
      const claimsData = (response as any)?.data ?? [];

      const newClaims: SelectedClaim[] = claimsData.map(
        mapClaimToSelectedClaim
      );

      setSelectedClaims((prev) => [...prev, ...newClaims]);
      setSelectedClaimOptions([]);
    } finally {
      setIsAddingClaims(false);
    }
  };

  const handleRemoveClaim = (claimUuid: string) => {
    setSelectedClaims((prev) => prev.filter((c) => c.claimUuid !== claimUuid));
  };

  const toggleExpand = (claimUuid: string) => {
    setSelectedClaims((prev) =>
      prev.map((c) =>
        c.claimUuid === claimUuid ? { ...c, expanded: !c.expanded } : c
      )
    );
  };

  const updateClaimField = (
    claimUuid: string,
    field: "status" | "paidAs",
    value: string
  ) => {
    setSelectedClaims((prev) =>
      prev.map((c) =>
        c.claimUuid === claimUuid ? { ...c, [field]: value } : c
      )
    );
  };

  const updateProcedureField = useCallback(
    (
      claimUuid: string,
      pcUuid: string,
      field: keyof ProcedureCode,
      value: number | string | boolean
    ) => {
      setSelectedClaims((prev) =>
        prev.map((c) =>
          c.claimUuid === claimUuid
            ? {
                ...c,
                patientProcedureCodes: c.patientProcedureCodes.map((pc) =>
                  pc.uuid === pcUuid ? { ...pc, [field]: value } : pc
                ),
              }
            : c
        )
      );
    },
    [setSelectedClaims]
  );
  return (
    <Box>
      {/* Select Claims Search */}
      <Text mb={1}>Select Claims</Text>
      <Flex gap={2} mb={3} alignItems="flex-start">
        <Box flex={1}>
          <MaskMultiSelectScroll<ClaimOption>
            value={selectedClaimOptions}
            onChange={(values) => setSelectedClaimOptions(values ?? [])}
            onSearch={(val) => {
              setSearchClaimKey(val);
              setClaimPagination((p) => ({ ...p, page: 0 }));
            }}
            options={claimOptions}
            loading={isFetchingClaims}
            pagination={claimPagination}
            setPagination={setClaimPagination}
            totalElements={(claimListData as any)?.data?.page?.totalElements}
            placeholder="Search by Claim ID, Patient Name"
            optionWithCheckbox
            limitTags={null}
            renderCustomLabel={(opt: ClaimOption) => (
              <Flex flexDirection="column" sx={{ minWidth: 0 }}>
                <Text fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>{opt.label}</Text>
                <Text variant="caption" sx={{ color: "text.secondary" }}>
                  {[opt.patientName, formatDate(opt.dateOfService)]
                    .filter(Boolean)
                    .join(" • ") || "-"}
                </Text>
              </Flex>
            )}
          />
        </Box>
        <StyledButtonV2
          label="Add"
          variant="outlined"
          onClick={handleAddClaims}
          disabled={selectedClaimOptions.length === 0 || isAddingClaims}
          sx={{ minWidth: "80px", height: "auto" }}
        />
      </Flex>

      <ScrollArea height={"calc(100vh - 300px)"}>
        {/* Selected Claims Table */}
        {selectedClaims.length > 0 && (
          <>
            <Text sx={{ fontWeight: DEFAULT_TEXT_FONT_WEIGHT }} mb={1}>
              Selected Claims
            </Text>
            <TableContainer
              sx={{
                overflowX: "hidden",
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: "grey.200",
                      "& th": {
                        fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
                        ...DEFAULT_FONTSIZE,
                        borderBottom: "1px solid",
                        borderColor: "grey.400",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell width={50} />
                    <TableCell>Claim ID</TableCell>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>Date Of Service</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell width={200}>Status</TableCell>
                    {/* Paid As is required to post a CPT line (handlePostCpt
                        blocks without it), so the header carries the asterisk
                        rather than only surfacing on save. */}
                    <TableCell width={200}>
                      Paid As
                      <Text
                        fontSize={"13px"}
                        component={"span"}
                        color={"error"}
                      >
                        {` *`}
                      </Text>
                    </TableCell>
                    <TableCell align="center">Posting</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedClaims.map((claim) => (
                    <ClaimRow
                      key={claim.claimUuid}
                      claim={claim}
                      onToggleExpand={toggleExpand}
                      onRemove={handleRemoveClaim}
                      onClaimFieldChange={updateClaimField}
                      onPostCpt={onPostCpt}
                      onUpdateProcedure={updateProcedureField}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </ScrollArea>
    </Box>
  );
};

// --- Claim Row Sub-component ---
interface ClaimRowProps {
  claim: SelectedClaim;
  onToggleExpand: (uuid: string) => void;
  onRemove: (uuid: string) => void;
  onClaimFieldChange: (
    uuid: string,
    field: "status" | "paidAs",
    value: string
  ) => void;
  onPostCpt: (claimUuid: string, pcUuid: string) => void;
  onUpdateProcedure: (
    claimUuid: string,
    pcUuid: string,
    field: keyof ProcedureCode,
    value: number | string | boolean
  ) => void;
}

const ClaimRow = ({
  claim,
  onToggleExpand,
  onRemove,
  onClaimFieldChange,
  onPostCpt,
  onUpdateProcedure,
}: ClaimRowProps) => {
  // Once any CPT line is posted, claim-level metadata (Status / Paid As) is
  // locked so every subsequent CPT post stays consistent. (ECRH-4700)
  const anyPosted = claim.patientProcedureCodes.some(isCptPosted);
  // Paid As stays editable until the whole claim is posted: while it is only
  // partially posted the remaining CPT lines still need it, and posting one
  // requires a Paid As value.
  const fullyPosted =
    getClaimPostingDisplay(claim.patientProcedureCodes, claim.postingStatus) ===
    "PAID";
  return (
    <>
      <TableRow
        sx={{
          "& td": {
            borderBottom: claim.expanded ? "none" : "1px solid",
            borderColor: "grey.300",
            py: 1,
          },
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={() => onToggleExpand(claim.claimUuid)}
          >
            {claim.expanded ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <Text isTabletext>{claim.claimId}</Text>
        </TableCell>
        <TableCell>
          <Text isTabletext>{claim.patientName}</Text>
        </TableCell>
        <TableCell>
          <Text isTabletext>{formatDate(claim.dateOfService) ?? "-"}</Text>
        </TableCell>
        <TableCell>
          <Input
            textFieldProps={{
              value: claim.provider,
              sx: { width: 140 },
              InputProps: { readOnly: true },
            }}
          />
        </TableCell>
        <TableCell>
          <CustomSelect
            options={CLAIM_STATUS_OPTIONS}
            value={claim.status}
            onChange={(value: Value) =>
              onClaimFieldChange(claim.claimUuid, "status", value ?? "")
            }
            readOnly={anyPosted}
          />
        </TableCell>
        <TableCell>
          <CustomSelect
            options={PAID_AS_OPTIONS}
            value={claim.paidAs}
            onChange={(value: Value) =>
              onClaimFieldChange(claim.claimUuid, "paidAs", value ?? "")
            }
            readOnly={fullyPosted}
          />
        </TableCell>
        {/* ECRH-4700 — claim posting status derived from per-CPT posting. */}
        <TableCell align="center">
          <ClaimPostingBadge
            pcs={claim.patientProcedureCodes}
            postingStatus={claim.postingStatus}
          />
        </TableCell>
        <TableCell align="center">
          {/* Claim-level Post removed — posting is per CPT row below. */}
          <IconButton
            size="small"
            color="error"
            onClick={() => onRemove(claim.claimUuid)}
            disabled={anyPosted}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Expandable Service Details */}
      <TableRow>
        <TableCell
          colSpan={9}
          sx={{
            p: 0,
            borderColor: "grey.300",
          }}
        >
          <Collapse in={claim.expanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, py: 2 }}>
              {claim.patientProcedureCodes.length === 0 ? (
                <Box
                  sx={{
                    py: 3,
                    textAlign: "center",
                    border: "1px dashed",
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <Text variant="body2" color="text.secondary">
                    No procedure codes available for this claim.
                  </Text>
                </Box>
              ) : (
                <ServiceDetailsTable
                  claim={claim}
                  onUpdateProcedure={onUpdateProcedure}
                  onPostCpt={onPostCpt}
                />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// --- Service Details Table Sub-component ---
const fieldSx = {
  "& .MuiInputBase-input": { ...DEFAULT_FONTSIZE, padding: "6px 8px" },
  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
};

const cellSx = { py: 1.5, px: 1 };

interface ServiceDetailsTableProps {
  claim: SelectedClaim;
  onUpdateProcedure: (
    claimUuid: string,
    pcUuid: string,
    field: keyof ProcedureCode,
    value: number | string | boolean
  ) => void;
  onPostCpt: (claimUuid: string, pcUuid: string) => void;
}

const ServiceDetailsTable = ({
  claim,
  onUpdateProcedure,
  onPostCpt,
}: ServiceDetailsTableProps) => {
  const totals = claim.patientProcedureCodes.reduce(
    (acc, pc) => {
      return {
        charge: acc.charge + (pc.charge || 0),
        quantity: acc.quantity + (pc.quantity || 0),
        insurancePaid: acc.insurancePaid + (pc.insurancePaid || 0),
        adjustment: acc.adjustment + (pc.adjustment || 0),
        writeOff: acc.writeOff + (pc.writeOff || 0),
        balance: acc.balance + getBalance(pc),
        copay: acc.copay + (pc.copay || 0),
        patientResponsibility:
          acc.patientResponsibility + getPatientResponsibility(pc),
        deductible: acc.deductible + (pc.deductible || 0),
      };
    },
    {
      charge: 0,
      quantity: 0,
      insurancePaid: 0,
      adjustment: 0,
      writeOff: 0,
      balance: 0,
      copay: 0,
      patientResponsibility: 0,
      deductible: 0,
    }
  );

  // ECRH-5391 — when a CPT is marked Denied, Denial Code and Denial Comment
  // are mandatory before that line can be posted. Errors surface only after a
  // Post attempt on the row.
  const [attemptedPost, setAttemptedPost] = useState<Record<string, boolean>>(
    {}
  );

  const postUpdateHandler = (id: any, type: any, value: any) => {
    onUpdateProcedure(claim.claimUuid, id.uuid, type, value);
    // Editing any field on the line retracts its validation errors; they come
    // back only if the next Post attempt still fails. Without this the error
    // stayed on screen after the user cleared the value it complained about.
    setAttemptedPost((prev) =>
      prev[id.uuid] ? { ...prev, [id.uuid]: false } : prev
    );
  };
  const getDenialErrors = (pc: ProcedureCode) => ({
    denialCode:
      pc.denied && !pc.denialCode.trim()
        ? "Denial Code is required"
        : undefined,
    denialComment:
      pc.denied && !pc.comments.trim()
        ? "Denial Comment is required"
        : undefined,
  });
  // ECRH-5571 Scenario 3 — a non-denied line whose Charge is not settled cannot
  // be posted, and says so. Denied lines settle through the denial code +
  // comments instead, so they are exempt.
  const getSettlementError = (pc: ProcedureCode) =>
    !pc.denied && !isChargeSettled(pc)
      ? "Balance must be 0 — settle the full Charge before posting"
      : undefined;
  const handlePostCpt = (pc: ProcedureCode) => {
    const errors = getDenialErrors(pc);
    const settlementError = getSettlementError(pc);
    if (errors.denialCode || errors.denialComment || settlementError) {
      setAttemptedPost((prev) => ({ ...prev, [pc.uuid]: true }));
      return;
    }
    onPostCpt(claim.claimUuid, pc.uuid);
  };

  // Field-level "required" validation removed from the post payment screen:
  // every service line field is optional. Post is blocked only by the ECRH-5571
  // settlement check and the denial rules.
  // Track which CPT fields the user has edited ("dirty") and which rows the
  // user has attempted to Post, so required errors surface only on interaction
  // instead of prematurely on load.
  // const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
  //   {}
  // );
  // const [attemptedPost, setAttemptedPost] = useState<Record<string, boolean>>(
  //   {}
  // );
  // const markTouched = (pcUuid: string, field: string) =>
  //   setTouchedFields((prev) => ({ ...prev, [`${pcUuid}:${field}`]: true }));

  return (
    <>
      <Text mb={1} fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
        Service Details
      </Text>
      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 1,
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            "& td, & th": { borderColor: "grey.200" },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "grey.100",
                "& th": {
                  fontSize: DEFAULT_FONTSIZE,
                  fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
                  py: 1.2,
                  px: 1,
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell width="11%">Item</TableCell>
              <TableCell width="5%">Charge</TableCell>
              <TableCell width="4%">Qty</TableCell>
              <TableCell width="7%">Payment</TableCell>
              <TableCell width="12%">Adjustment</TableCell>
              <TableCell width="12%">Write-off</TableCell>
              <TableCell width="19%" colSpan={3} align="center">
                Patient Responsibility
              </TableCell>
              {/* ECRH-5391 — Balance sits immediately after Patient Responsibility. */}
              <TableCell width="5%">Balance</TableCell>
              <TableCell width="12%">Comments</TableCell>
              <TableCell width="8%" align="center">
                Denied
              </TableCell>
              <TableCell width="7%" align="center">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {claim.patientProcedureCodes.map((pc) => {
              const balance = getBalance(pc);
              // When a CPT is marked Denied, every financial input up to Balance
              // is locked; only Comments and Denial Code stay editable.
              const posted = isCptPosted(pc);
              const lockFinancials = posted || pc.denied;
              // ECRH-5391 — denial-field errors, shown only after a Post attempt.
              const denialErrors = attemptedPost[pc.uuid]
                ? getDenialErrors(pc)
                : { denialCode: undefined, denialComment: undefined };
              // ECRH-5571 — settlement error, shown only after a Post attempt.
              const settlementError = attemptedPost[pc.uuid]
                ? getSettlementError(pc)
                : undefined;

              // Validation removed — no field on a service line is required and
              // no error is surfaced. Restore the block below to bring back the
              // required checks on Payment / Adjustment / Copay / Co-Insurance
              // and the denial-code rules.
              //
              // const required = pc.denied
              //   ? undefined
              //   : (val: number) => (val <= 0 ? "Required" : undefined);
              // const denialCode = pc.denialCode.trim();
              // const rawErrors: Record<string, string | undefined> = {
              //   insurancePaid:
              //     required?.(pc.insurancePaid) ??
              //     (pc.insurancePaid > pc.charge
              //       ? "Payment cannot exceed Charge"
              //       : undefined),
              //   adjustment: required?.(pc.adjustment),
              //   copay: required?.(pc.copay),
              //   coInsurance: required?.(pc.coInsurance),
              //   denialCode: !pc.denied
              //     ? undefined
              //     : denialCode === ""
              //       ? "Denial Code is Required"
              //       : !DENIAL_CODE_PATTERN.test(denialCode)
              //         ? "Only letters, numbers and hyphens"
              //         : undefined,
              // };
              // const hasValidationError = Object.values(rawErrors).some(Boolean);
              // const fieldError = (field: string) =>
              //   touchedFields[`${pc.uuid}:${field}`] || attemptedPost[pc.uuid]
              //     ? rawErrors[field]
              //     : undefined;

              return (
                <TableRow
                  key={pc.uuid}
                  sx={{
                    verticalAlign: "top",
                    "& td": {
                      ...cellSx,
                      borderBottom: "1px solid",
                      borderColor: "grey.200",
                    },
                  }}
                >
                  {/* Item */}
                  <TableCell>
                    <Box
                      sx={{
                        bgcolor: PRIMITIVE.color.neutrals[200],
                        border: "1px solid",
                        borderColor: PRIMITIVE.color.neutrals[1000],
                        borderRadius: "4px",
                        px: 1,
                        py: "6px",
                      }}
                    >
                      <RenderWithTooltip
                        value={
                          pc.cptCode +
                          (pc.description ? ` — ${pc.description}` : "")
                        }
                      />
                    </Box>
                  </TableCell>
                  {/* Charge */}
                  <TableCell>
                    <Text
                      variant="body2"
                      fontWeight={DEFAULT_TEXT_FONT_WEIGHT}
                      lineHeight="32px"
                    >
                      {Number(pc.charge).toFixed(2)}
                    </Text>
                  </TableCell>
                  {/* Quantity */}
                  <TableCell>
                    <Text
                      variant="body2"
                      fontWeight={DEFAULT_TEXT_FONT_WEIGHT}
                      lineHeight="32px"
                    >
                      {pc.quantity ?? "-"}
                    </Text>
                  </TableCell>
                  {/* Payment */}
                  <TableCell>
                    <NumberInput
                      error={settlementError}
                      textFieldProps={{
                        size: "small",
                        value: pc.insurancePaid || "",
                        onChange: (e) => {
                          postUpdateHandler(
                            pc,
                            "insurancePaid",
                            Number(e.target.value) || 0
                          );
                        },
                        sx: { width: "100%", ...fieldSx },
                        disabled: lockFinancials,
                      }}
                    />
                  </TableCell>
                  {/* Adjustment + type dropdown */}
                  <TableCell>
                    <Flex flexDirection="column" gap={1.5}>
                      <NumberInput
                        error={Boolean(settlementError)}
                        textFieldProps={{
                          size: "small",
                          value: pc.adjustment || "",
                          onChange: (e) => {
                            postUpdateHandler(
                              pc,
                              "adjustment",
                              Number(e.target.value) || 0
                            );
                          },
                          sx: { width: "100%", ...fieldSx },
                          disabled: lockFinancials,
                        }}
                      />
                      <CustomSelect
                        options={ADJUSTMENT_TYPE_OPTIONS}
                        value={pc.adjustmentType}
                        onChange={(value: Value) =>
                          postUpdateHandler(pc, "adjustmentType", value ?? "")
                        }
                        readOnly={lockFinancials}
                      />
                    </Flex>
                  </TableCell>
                  {/* Write-off + type dropdown */}
                  <TableCell>
                    <Flex flexDirection="column" gap={1.5}>
                      <NumberInput
                        error={Boolean(settlementError)}
                        textFieldProps={{
                          size: "small",
                          value: pc.writeOff || "",
                          onChange: (e) => {
                            postUpdateHandler(
                              pc,
                              "writeOff",
                              Number(e.target.value) || 0
                            );
                          },
                          sx: { width: "100%", ...fieldSx },
                          disabled: lockFinancials,
                        }}
                      />
                      {/* ECRH-4815 */}
                      {/* <CustomSelect
                        options={WRITEOFF_TYPE_OPTIONS}
                        value={pc.writeOffType}
                        onChange={(value: Value) =>
                          postUpdateHandler(
                            pc,
                            "writeOffType",
                            value ?? ""
                          )
                        }
                        readOnly={posted}
                      /> */}
                    </Flex>
                  </TableCell>
                  {/* Copay */}
                  <TableCell>
                    <Flex flexDirection="column" gap={1}>
                      <NumberInput
                        error={Boolean(settlementError)}
                        textFieldProps={{
                          size: "small",
                          value: pc.copay || "",
                          onChange: (e) => {
                            postUpdateHandler(
                              pc,
                              "copay",
                              Number(e.target.value) || 0
                            );
                          },
                          sx: { width: "100%", ...fieldSx },
                          disabled: lockFinancials,
                        }}
                      />
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: "4px",
                          px: 1,
                          py: 0.5,
                          textAlign: "center",
                        }}
                      >
                        <Text variant="caption" color="text.secondary">
                          Copay
                        </Text>
                      </Box>
                    </Flex>
                  </TableCell>
                  {/* Co-Insurance */}
                  <TableCell>
                    <Flex flexDirection="column" gap={1}>
                      <NumberInput
                        error={Boolean(settlementError)}
                        textFieldProps={{
                          size: "small",
                          value: pc.coInsurance || "",
                          onChange: (e) => {
                            postUpdateHandler(
                              pc,
                              "coInsurance",
                              Number(e.target.value) || 0
                            );
                          },
                          sx: { width: "100%", ...fieldSx },
                          disabled: lockFinancials,
                        }}
                      />
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: "4px",
                          px: 1,
                          py: 0.5,
                          textAlign: "center",
                        }}
                      >
                        <Text variant="caption" color="text.secondary">
                          Co-Ins
                        </Text>
                      </Box>
                    </Flex>
                  </TableCell>
                  {/* Deductible */}
                  <TableCell>
                    <Flex flexDirection="column" gap={1}>
                      <NumberInput
                        error={Boolean(settlementError)}
                        textFieldProps={{
                          size: "small",
                          value: pc.deductible || "",
                          onChange: (e) =>
                            postUpdateHandler(
                              pc,
                              "deductible",
                              Number(e.target.value) || 0
                            ),
                          sx: { width: "100%", ...fieldSx },
                          disabled: lockFinancials,
                        }}
                      />
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: "4px",
                          px: 1,
                          py: 0.5,
                          textAlign: "center",
                        }}
                      >
                        <Text variant="caption" color="text.secondary">
                          Deductible
                        </Text>
                      </Box>
                    </Flex>
                  </TableCell>
                  {/* Balance — ECRH-5391 */}
                  <TableCell>
                    <Text
                      variant="body2"
                      fontWeight={DEFAULT_TEXT_FONT_WEIGHT}
                      lineHeight="32px"
                    >
                      {balance.toFixed(2)}
                    </Text>
                  </TableCell>
                  {/* Comments */}
                  <TableCell>
                    <Input
                      textFieldProps={{
                        multiline: true,
                        minRows: 2,
                        value: pc.comments,
                        onChange: (e) =>
                          postUpdateHandler(pc, "comments", e.target.value),
                        sx: { width: "100%" },
                        InputProps: { readOnly: posted },
                        error: Boolean(denialErrors.denialComment),
                        helperText: denialErrors.denialComment,
                      }}
                    />
                  </TableCell>
                  {/* Denied + Denial Code (ECRH-5391) */}
                  <TableCell align="left">
                    <Checkbox
                      size="small"
                      checked={pc.denied}
                      disabled={posted}
                      onChange={(e) =>
                        postUpdateHandler(pc, "denied", e.target.checked)
                      }
                    />
                    {pc.denied && (
                      <Input
                        textFieldProps={{
                          size: "small",
                          placeholder: "Denial Code",
                          value: pc.denialCode,
                          onChange: (e) => {
                            postUpdateHandler(pc, "denialCode", e.target.value);
                          },
                          sx: { width: "100%", ...fieldSx },
                          InputProps: { readOnly: posted },
                          error: Boolean(denialErrors.denialCode),
                          helperText: denialErrors.denialCode,
                        }}
                      />
                    )}
                  </TableCell>
                  {/* Post (per CPT) — ECRH-4700 */}
                  <TableCell align="center">
                    <StyledButtonV2
                      label={posted ? "Posted" : "Post"}
                      variant="outlined"
                      disabled={posted}
                      onClick={() => handlePostCpt(pc)}
                      sx={{
                        minWidth: "60px",
                        height: "30px",
                        border: 2,
                        borderRadius: "12px",
                        ...DEFAULT_FONTSIZE,
                        ...(posted && {
                          bgcolor: "success.light",
                          color: "success.dark",
                          borderColor: "success.main",
                          "&.Mui-disabled": {
                            bgcolor: "success.light",
                            color: "success.dark",
                            borderColor: "success.main",
                          },
                        }),
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Totals Row */}
            <TableRow
              sx={{
                bgcolor: "grey.100",
                "& td": {
                  ...cellSx,
                  fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
                  ...DEFAULT_FONTSIZE,
                  borderTop: "1px solid",
                  borderColor: "grey.300",
                  borderBottom: "none",
                },
              }}
            >
              <TableCell>
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  Total
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  {totals.charge.toFixed(2)}
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  {totals.quantity}
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  {totals.insurancePaid.toFixed(2)}
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  {totals.adjustment.toFixed(2)}
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  {totals.writeOff.toFixed(2)}
                </Text>
              </TableCell>
              <TableCell colSpan={3} align="center">
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  {totals.patientResponsibility.toFixed(2)}
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="body2" fontWeight={DEFAULT_TEXT_FONT_WEIGHT}>
                  {totals.balance.toFixed(2)}
                </Text>
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SelectedClaimsSection;
